import { useEffect, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { ApiError, updateAccountSettings, type AuthMe } from './api';
import type { FeatureKey } from '../shared/account';
import type { StoryLength } from '../shared/story-length';

type ProfileModalProps = {
  account: Extract<AuthMe, { authenticated: true }>;
  onClose: () => void;
  onSaved: (account: Extract<AuthMe, { authenticated: true }>) => void;
  onLogout: () => Promise<void>;
  onOpenBilling: () => void;
};

function messageFromError(cause: unknown) {
  if (cause instanceof ApiError) return cause.message;
  return cause instanceof Error ? cause.message : '保存失败，请稍后重试。';
}

const FEATURE_LABELS: Record<FeatureKey, string> = {
  image: '配图组件',
  audio: '配音组件',
  video: '视频组件',
};

const STORY_LENGTH_LABELS: Record<StoryLength, string> = { short: '短篇', medium: '中篇', long: '长篇' };

export function ProfileModal({ account, onClose, onSaved, onLogout, onOpenBilling }: ProfileModalProps) {
  const [nickname, setNickname] = useState(account.nickname);
  const [bio, setBio] = useState(account.bio);
  const [preferences, setPreferences] = useState({ ...account.preferences });
  const [saving, setSaving] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [error, setError] = useState('');
  const busy = saving || loggingOut;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [busy, onClose]);

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      const updated = await updateAccountSettings(nickname, bio, preferences);
      onSaved(updated);
      onClose();
    } catch (cause) {
      setError(messageFromError(cause));
    } finally {
      setSaving(false);
    }
  }

  async function handleLogout() {
    if (!confirmLogout) {
      setConfirmLogout(true);
      return;
    }
    setError('');
    setLoggingOut(true);
    try {
      await onLogout();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '退出登录失败，请稍后重试。');
      setLoggingOut(false);
    }
  }

  return createPortal(
    <div className="modal-backdrop auth-modal-backdrop profile-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
      <section className="continuation-modal profile-modal" role="dialog" aria-modal="true" aria-labelledby="profile-modal-title">
        <div className="modal-heading">
          <div><span className="section-kicker">个人资料</span><h2 id="profile-modal-title">个人信息<br /><em>写下你的名字。</em></h2></div>
          <button className="modal-close" disabled={busy} type="button" onClick={onClose} aria-label="关闭个人信息窗口">×</button>
        </div>
        <p className="modal-description">这些信息只会用于展示你的故事空间，不会改变已经生成的小说内容。</p>
        <form className="profile-form" onSubmit={handleSave}>
          <p className="profile-phone"><span>登录手机号</span><strong>{account.maskedPhone}</strong></p>
          <button className="profile-balance" disabled={busy} type="button" onClick={() => { onClose(); onOpenBilling(); }}><span>账户余额<small>查看历史账单 →</small></span><strong>{account.balanceText}</strong></button>
          <label className="modal-input-label">昵称<input autoComplete="nickname" name="nickname" maxLength={40} placeholder="给自己取一个名字…" value={nickname} onChange={(event) => setNickname(event.target.value)} /></label>
          <label className="modal-input-label">个人简介<span>可选</span><textarea autoComplete="off" name="bio" maxLength={240} placeholder="介绍一下你自己…" value={bio} onChange={(event) => setBio(event.target.value)} /></label>
          <fieldset className="profile-features">
            <legend>创作组件</legend>
            {(['image', 'audio', 'video'] as FeatureKey[]).map((feature) => {
              const effective = account.features[feature];
              const disabled = !effective.available || !effective.allowed;
              return <label className="profile-feature-row" key={feature}>
                <span>{FEATURE_LABELS[feature]}<small>{disabled ? effective.reason : preferences[feature] ? '已开启' : '已关闭'}</small></span>
                <input
                  aria-label={FEATURE_LABELS[feature]}
                  checked={preferences[feature]}
                  disabled={busy || disabled}
                  type="checkbox"
                  onChange={(event) => setPreferences((current) => ({ ...current, [feature]: event.target.checked }))}
                />
              </label>;
            })}
          </fieldset>
          <div className="profile-story-lengths"><span>篇幅权限</span><strong>{(account.storyLengths ?? ['short']).map((length) => STORY_LENGTH_LABELS[length]).join('、')}</strong><small>{account.plan.key === 'beta' ? '基础用户可生成短篇，会员可解锁中长篇。' : '当前等级已开放对应篇幅。'}</small></div>
          {error && <p className="auth-error" role="alert">{error}</p>}
          <div className="modal-actions profile-modal-actions"><button className="modal-cancel" disabled={busy} type="button" onClick={handleLogout}>{loggingOut ? '正在退出…' : '退出登录'}</button><button className="modal-submit" disabled={busy} type="submit">{saving ? '正在保存…' : '保存设置 →'}</button></div>
          {confirmLogout && !busy && <div className="profile-logout-confirm" role="alert"><span>确定要退出当前账号吗？</span><button type="button" onClick={() => setConfirmLogout(false)}>取消</button><button type="button" onClick={() => void handleLogout()}>确定退出</button></div>}
        </form>
      </section>
    </div>,
    document.body,
  );
}
